import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Output,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface SegmentOption {
  value: string;
  label: string;
  disabled?: boolean;
}

/**
 * Segmentschalter: a few mutually exclusive views as one pill track; the
 * chosen one lies pressed in (CI `.al-pressed` face), never a blue fill.
 * A radiogroup — arrow keys move and choose, one tab stop. Usable with
 * `[(ngModel)]`/forms (CVA) or `[value]` + `(valueChange)`.
 */
@Component({
  selector: 'app-segmented-control',
  template: `
    <div class="track" role="radiogroup" [attr.aria-label]="ariaLabel" (keydown)="onKey($event)">
      <button
        *ngFor="let o of options; let i = index; trackBy: trackOption"
        type="button"
        role="radio"
        class="segment"
        [class.is-selected]="o.value === value"
        [attr.aria-checked]="o.value === value"
        [attr.tabindex]="o.value === value || (!hasValue && i === 0) ? 0 : -1"
        [disabled]="disabled || o.disabled"
        (click)="choose(o)"
      >
        {{ o.label }}
      </button>
    </div>
  `,
  styleUrl: './segmented-control.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SegmentedControlComponent), multi: true },
  ],
})
export class SegmentedControlComponent implements ControlValueAccessor {
  @Input() options: SegmentOption[] = [];
  @Input() value: string | null = null;
  @Input() ariaLabel = '';
  @Input() disabled = false;
  @Output() valueChange = new EventEmitter<string>();

  private onChange: (v: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private cdr: ChangeDetectorRef, private host: ElementRef<HTMLElement>) {}

  get hasValue(): boolean {
    return this.options.some((o) => o.value === this.value);
  }

  trackOption(_i: number, o: SegmentOption): string {
    return o.value;
  }

  choose(o: SegmentOption): void {
    if (this.disabled || o.disabled || o.value === this.value) return;
    this.value = o.value;
    this.onChange(o.value);
    this.onTouched();
    this.valueChange.emit(o.value);
    this.cdr.markForCheck();
  }

  onKey(event: KeyboardEvent): void {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
    const edge = event.key === 'Home' ? 'first' : event.key === 'End' ? 'last' : null;
    if (!step && !edge) return;
    const enabled = this.options.filter((o) => !o.disabled);
    if (!enabled.length) return;
    event.preventDefault();
    let next: SegmentOption;
    if (edge === 'first') next = enabled[0];
    else if (edge === 'last') next = enabled[enabled.length - 1];
    else {
      const at = Math.max(0, enabled.findIndex((o) => o.value === this.value));
      next = enabled[(at + step! + enabled.length) % enabled.length];
    }
    this.choose(next);
    const index = this.options.indexOf(next);
    setTimeout(() => (this.host.nativeElement.querySelectorAll('button')[index] as HTMLElement | undefined)?.focus());
  }

  writeValue(value: string | null): void {
    this.value = value;
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (v: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    this.cdr.markForCheck();
  }
}
